export const spacing = {
  xs: "gap-1",
  sm: "gap-2",
  md: "gap-3",
  lg: "gap-4",
  xl: "gap-6",
};

export const radii = {
  sm: "rounded-lg",
  md: "rounded-xl",
  lg: "rounded-2xl",
};

export const shadows = {
  soft: "shadow-sm",
  card: "shadow-md",
};

/** sm / md / lg: feste Höhen für konsistente Toolbars; icon: Touch-Ziel 44px */
export const buttonSizes = {
  sm: "text-xs h-8 px-2.5 gap-1.5",
  md: "text-sm h-9 px-3 gap-2",
  lg: "text-sm h-11 min-h-[44px] px-4 gap-2",
  icon: "h-10 w-10 min-h-[44px] min-w-[44px] p-0 gap-0 shrink-0",
};

/**
 * Interner Navigations‑`Link`/`a` mit gleicher Grundoptik wie
 * `<AppButton variant="secondary" size="sm" />` (Rand, Radius, h-8).
 */
export const appButtonSecondarySmLinkClassName = [
  "wrtk-btn inline-flex items-center justify-center rounded-lg border font-medium transition-colors duration-150 focus-ring btn-secondary no-underline !border-white/10 hover:!bg-white/[0.06]",
  buttonSizes.sm,
].join(" ");

/** Enterprise: Mindest-Touch-Höhe 44px (WCAG) — Basis für Nav, Sort-Trigger, Filter */
export const enterpriseTouchMinClassName = "min-h-[44px]";

/**
 * Listen-/Detail-Kopf: gleiche sichtbare Höhe wie `size="lg"` (`h-11`),
 * kein automatischer Zeilenumbruch — sonst wirkt `variant="primary"` höher als Secondary.
 */
/** Mobile: kompakte Header-Nav (4er-Zeile) — Desktop unverändert h-11 */
export const detailPageHeaderNavMobileCompactClassName =
  "max-md:!min-h-9 max-md:!h-9 max-md:!max-h-9 max-md:!px-2 max-md:!text-[11px] max-md:justify-center max-md:font-normal";

export const detailPageHeaderNavTouchClassName = `${enterpriseTouchMinClassName} h-11 max-h-11 shrink-0 whitespace-nowrap ${detailPageHeaderNavMobileCompactClassName}`;

/** Nur für `variant="secondary"` in derselben Kopfzeile: dezente Kanten wie Eingabefelder */
export const detailPageHeaderNavSecondaryShellClassName = "!border-white/10 hover:!bg-white/[0.06]";

/**
 * Karten-Inhalt: gleiche Sekundär-Chrom-Schiene wie Header-`Link` („Zum Kunden“),
 * aber niedriger — „Öffnen“ / „Route“ in Info-Spalten ohne viel Höhe.
 */
export const detailCardCompactSecondaryLinkClassName = [
  appButtonSecondarySmLinkClassName,
  detailPageHeaderNavSecondaryShellClassName,
  "!min-h-0 !h-7 !py-0 !px-2 !text-[11px] !gap-1 !leading-tight !rounded-md",
  "!no-underline",
].join(" ");

/** `variant="secondary"` + `size="lg"` — Touch + Sekundär-Optik (nicht auf `success`/`primary`; dort nur `detailPageHeaderNavTouchClassName`, sonst überschreibt `hover:!bg-white/…` die Variantenfarben). */
export const detailPageHeaderNavClassName = `${detailPageHeaderNavTouchClassName} ${detailPageHeaderNavSecondaryShellClassName}`;

/** Listen-/Detail-Kopf: Nav-Buttons — Mobile 4-Spalten, ab sm wie bisher flex */
export const detailPageHeaderNavActionsClassName =
  "grid grid-cols-4 gap-1.5 w-full sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:w-auto sm:gap-2";

/** Primary-Aktion in derselben Kopfzeile: Mobile volle Breite, ab sm inline */
export const detailPageHeaderNavPrimaryMobileClassName = "max-md:col-span-4 max-md:w-full";

/** Listen: Sort-Button (zusätzlich `variant="secondary"`, `ref` möglich) */
export const listSortTriggerClassName = "min-h-[44px] px-3 py-2 font-normal whitespace-nowrap";

/** Toolbar-Filter / Gruppierung: kompakt, gleiche Höhe wie `size="sm"` + `entityRowCompactActionClassName` */
export const listCompactFilterTriggerClassName =
  "!min-h-7 !h-7 !max-h-7 !px-2.5 !py-0 text-xs font-normal whitespace-nowrap shrink-0 justify-center";

/** Segment-/Bereichs-Chips: „Meine Aufgaben“ (Ansicht + Kategorien), Leitstand „Bereich“ — Radius wie andere `rounded-lg`-Buttons */
export const segmentTabChipButtonClassName =
  "px-2.5 py-1 rounded-lg text-xs border transition font-normal min-h-0 h-auto";

/**
 * Mobile-Ergänzung für Segment-Chips — Desktop-Chip unverändert (`segmentTabChipButtonClassName` allein).
 * Kombination: `${segmentTabChipButtonClassName} ${segmentTabChipButtonMobileClassName}`
 */
export const segmentTabChipButtonMobileClassName =
  "max-md:min-h-[44px] max-md:px-3 max-md:py-2 max-md:text-sm max-md:whitespace-normal max-md:text-center max-md:leading-snug max-md:justify-center";

/** Meine Aufgaben: kompakte Segment-Chips nur Mobile (Desktop nutzt segmentTabChipButtonClassName allein) */
export const meineAufgabenMobileSegmentChipClassName =
  "max-md:!min-h-9 max-md:!h-auto max-md:px-2 max-md:py-1.5 max-md:text-[11px] max-md:leading-tight max-md:whitespace-normal max-md:text-center max-md:justify-center";

/** Seiten-Wrapper: verhindert horizontales Überlaufen nur unter md */
export const mobileOverflowGuardClassName = "mobile-overflow-guard";

/** Sticky-Footer / Action-Bar: Safe-Area unten nur unter md */
export const mobileSafeAreaBottomClassName = "mobile-safe-area-bottom";

/**
 * Formularfelder auf Mobile: min. 16px Schrift (verhindert iOS-Auto-Zoom beim Fokus).
 * Global in index.css unter 768px; Klasse für gezielte Overrides in Komponenten.
 */
export const mobileFormControlClassName = "max-md:text-base max-md:min-h-[44px]";

/** Standard-Innenabstand für ResponsivePageContainer (Desktop py-6 unverändert) */
export const mobilePagePaddingClassName = "px-3 py-4 sm:px-4 md:px-4 md:py-6";

/** MobileActionBar-Innenbereich (Touch-freundliche Abstände) */
export const mobileActionBarInnerClassName =
  "backdrop-blur bg-surface-elevated border border-default rounded-2xl p-3 shadow-xl";

/** Filter-Sheet: Abstand zwischen Filter-Zeilen (nur Mobile-Pfad in FilterBarOrDrawer) */
export const mobileFilterDrawerStackClassName = "flex flex-col gap-3 py-1.5";

/** Filter-Sheet: einheitliche Trigger-Buttons (Falltyp, Status, Sortierung …) */
export const filterDrawerTriggerClassName =
  "text-sm w-full min-h-[40px] justify-center font-normal";

/**
 * Collapsible-Blöcke (Dateien, Stunden): Mobile kompakt & einheitlich (h-9),
 * Suche mit 16px Schrift gegen iOS-Zoom.
 */
export const blockSectionToolbarMobileNormalizeClassName =
  "[&_input:not([type=checkbox])]:max-md:!min-h-9 [&_input:not([type=checkbox])]:max-md:!h-9 [&_input:not([type=checkbox])]:max-md:!py-1.5 [&_input:not([type=checkbox])]:max-md:text-base [&_select]:max-md:!min-h-9 [&_select]:max-md:!h-9 [&_select]:max-md:!py-1.5 [&_select]:max-md:!text-xs [&_.wrtk-btn]:max-md:!min-h-9 [&_.wrtk-btn]:max-md:!h-9 [&_.wrtk-btn]:max-md:!px-2.5 [&_.wrtk-btn]:max-md:!text-xs";

/** Filter-Zeile in BlockSectionToolbar — Mobile horizontal scroll, Desktop inline */
export const blockSectionToolbarFiltersRowClassName =
  "flex flex-wrap items-stretch gap-2 max-md:flex max-md:flex-nowrap max-md:gap-1.5 max-md:w-full max-md:overflow-x-auto max-md:overscroll-x-contain max-md:[-webkit-overflow-scrolling:touch] max-md:pb-0.5 md:contents";

/** Filter-Selects — Mobile feste Mindestbreite + Scroll (nicht flex-1), Desktop kompakt */
export const blockSectionToolbarFilterSelectClassName =
  "text-xs shrink-0 md:!min-h-9 md:!py-1.5 md:!px-2 md:min-w-[8.25rem] md:max-w-[10.5rem] md:w-auto max-md:flex-none max-md:min-w-[8.25rem] max-md:max-w-[10rem] max-md:w-auto max-md:!h-9 max-md:!min-h-9 max-md:!text-xs max-md:!px-2 max-md:!py-1.5";

/** Zusatz-Buttons in der Filter-Zeile (✕ Filter, Woche erfassen) — nicht mitflexen */
export const blockSectionToolbarFilterExtraButtonClassName =
  "shrink-0 max-md:flex-none max-md:whitespace-nowrap";

/** Schnellwahl-Chips (Desktop) — nur horizontal scrollen, keine Zeilenumbrüche */
export const blockSectionQuickChipsRowClassName =
  "mb-2 flex flex-nowrap items-center gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain pb-0.5 [-webkit-overflow-scrolling:touch]";

/** Kompakter Chip-Button in blockSectionQuickChipsRow */
export const blockSectionQuickChipButtonClassName =
  "seg-btn-idle !min-h-8 !h-8 !max-h-8 !px-2 !py-0 !text-[11px] shrink-0 whitespace-nowrap";

/**
 * Auftragskarten („Meine Aufgaben“, Übersicht): „Archivieren“.
 * Mit `AppButton variant="secondary" size="sm"` — keine `rounded`-Minimal-Radien und kein Ghost mit Fake-Rand (sonst eckig/uneinheitlich).
 */
export const entityCardArchiveButtonClassName = "mt-0.5 font-medium text-[11px] sm:!text-xs disabled:opacity-60";

/** Entity-Karten: kompakteres Mobile-Padding */
export const entityCardShellClassName = "max-md:px-3 max-md:py-2";

/** Mobile Entity-Karten: Titelzeile — clamp-2, bricht auch sehr lange Strings ohne Leerzeichen */
export const entityCardTitleClassName =
  "min-w-0 flex-1 w-full max-w-full overflow-hidden line-clamp-2 break-words [overflow-wrap:anywhere] text-[15px] sm:text-[17px] leading-5 sm:leading-6 font-semibold text-blue-100";

/** Mobile Entity-Karten: kompakter Archivieren-Button */
export const entityCardArchiveButtonMobileClassName =
  "max-md:!mt-0 max-md:!min-h-[28px] max-md:!h-7 max-md:!px-2 max-md:!py-0 max-md:!text-[10px] max-md:leading-none max-md:shrink-0";

/** Einheitlicher Loading-Zustand für `AppButton variant="success"` (Speichern/Erstellen) */
export const successButtonLoadingClassName =
  "!bg-green-300/40 !border-green-300/30 cursor-not-allowed hover:!bg-green-300/40";

/** Erstellen/Bearbeiten-Formulare: grüner Submit wie EntityFormEngine */
export const entityFormSubmitPrimaryClassName =
  "!bg-green-500 hover:!bg-green-600 !border-green-500/50";

export const entityFormSubmitDisabledClassName = successButtonLoadingClassName;

/** Dateien & ähnliche Listen: Vorschau — gleiche `size="sm"`-Höhe wie Speichern, dezent (`variant="secondary"` + diese Klassen) */
export const fileListPreviewActionClassName =
  "!font-medium shrink-0 whitespace-nowrap !text-blue-200 hover:!bg-white/12";

/**
 * Tonales „Löschen“ (nicht gefülltes `btn-danger` / `--danger`):
 * gleiche Idle/Hover-Border/Text/Füllung für Dateizuilen, Mehr-Menü „Löschen“, kompakte Zeilen.
 */
export const destructiveTonalInteractiveClassName =
  "!border-red-400/35 !bg-red-500/10 !text-red-300 hover:!bg-red-500/18";

/** Dateien & ähnliche Listen: Löschen — wie Vorschau, rot unterlegt */
export const fileListDeleteActionClassName =
  "!font-medium shrink-0 whitespace-nowrap " + destructiveTonalInteractiveClassName;

/** `AppButton variant="violet"` — Loading (Final speichern / Meta) */
export const violetButtonLoadingClassName =
  "!bg-violet-300/40 !border-violet-300/30 cursor-not-allowed hover:!bg-violet-300/40";

/** Checklisten: Ghost-Button „Erledigt“ (filled) — wie WartungsvertragDetail */
export const checklistGhostDoneClassName =
  "!bg-green-600 !text-white !border-green-500 !border-2 shadow-none";

/** Checklisten: Ghost offen — Dark-UI */
export const checklistGhostOpenClassNameDark =
  "!bg-green-500/10 !border-2 !border-green-500/60 !text-green-300 hover:!bg-green-500/20 !shadow-none";

/** @param {boolean} erledigt @param {boolean} [_isDark] Legacy-Parameter (ignoriert) */
export function checklistErledigenGhostClasses(erledigt, _isDark) {
  if (erledigt) return checklistGhostDoneClassName;
  return checklistGhostOpenClassNameDark;
}

/** @param {boolean} aktiv @param {boolean} [_isDark] Legacy */
export function checklistNichtNoetigGhostClasses(aktiv, _isDark) {
  if (aktiv) return "!bg-amber-600 !text-white !border-amber-500 !border-2 !shadow-none";
  return "!bg-amber-500/10 !border-2 !border-amber-500/60 !text-amber-200 hover:!bg-amber-500/20 !shadow-none";
}

/** @param {boolean} aktiv @param {boolean} [_isDark] Legacy */
export function checklistInArbeitGhostClasses(aktiv, _isDark) {
  if (aktiv) return "!bg-blue-600 !text-white !border-blue-500 !border-2 !shadow-none";
  return "!bg-sky-500/10 !border-2 !border-sky-500/60 !text-sky-200 hover:!bg-sky-500/20 !shadow-none";
}

/**
 * Checklisten-Karten: linker Status-Streifen (Ampel nach Zustand).
 * @param {{ erledigt?: boolean, nicht_noetig?: boolean, in_arbeit?: boolean }} item
 * @param {boolean} [_isDark] Legacy
 */
export function checklistItemStripeClassName(item, _isDark) {
  if (item?.erledigt) return "bg-emerald-400";
  if (item?.nicht_noetig) return "bg-amber-400";
  if (item?.in_arbeit) return "bg-sky-400";
  return "bg-slate-500/55";
}

/** Schmaler linker Balken, zur Kartenfläche ohne Verlauf. */
export function checklistItemStripeRailClassName(item, _isDark) {
  return `w-1 shrink-0 self-stretch ${checklistItemStripeClassName(item, _isDark)}`;
}

/** Einheitliche Checklisten-Karte: flach, gleiche Tönung wie Panel-Hintergrund */
export function checklistItemCardShellClassName(_isDark) {
  return "border-white/10 bg-white/[0.03] hover:bg-white/[0.045] hover:border-white/14";
}

export const pillSizes = {
  xs: "text-[10px] px-2 py-0.5 md:text-[10px] md:px-2 md:py-0.5",
  sm: "text-[11px] px-2 py-0.5 md:text-xs md:px-2.5 md:py-1",
  status: "text-[11px] px-2 py-0.5 md:text-xs md:px-2.5 md:py-1",
};

/** Checkbox-/Radio-Zeile wie Wartungsvertrag-Detail („Reminder“): Kachel mit Rand und aktiver Tönung. */
export function wartungRulesChoiceRowClass(selected) {
  return [
    "flex items-center gap-2.5 text-sm rounded-lg border px-2.5 py-2 cursor-pointer transition select-none",
    selected ? "border-blue-400/35 bg-blue-500/10 text-blue-50" : "border-white/10 bg-white/[0.02] text-blue-100/90 hover:bg-white/[0.05]",
  ].join(" ");
}

/** Gleiche Farben wie `wartungRulesChoiceRowClass`, aber flacher und nur so breit wie Inhalt — z. B. Spesen-Toggle. */
export function wartungRulesChoiceRowCompactClass(selected) {
  return [
    "inline-flex items-center gap-2 max-w-full text-sm rounded-md border px-2 py-1.5 cursor-pointer transition select-none",
    selected ? "border-blue-400/35 bg-blue-500/10 text-blue-50" : "border-white/10 bg-white/[0.02] text-blue-100/90 hover:bg-white/[0.05]",
  ].join(" ");
}

/**
 * Auslöser „…“: optisch wie Kopf-„Zum Kunden“ (`variant="secondary"` + Shell), kompakteres Padding.
 */
export const entityMoreMenuTriggerClassName = [
  "inline-flex items-center justify-center shrink-0 !gap-0",
  "min-h-[44px] sm:min-h-0 min-w-[44px] sm:min-w-0",
  "max-md:!min-h-8 max-md:!h-8 max-md:!max-h-8 max-md:!min-w-8 max-md:!w-8 max-md:!px-0",
  "!px-2.5",
  detailPageHeaderNavSecondaryShellClassName,
  "!shadow-none",
  "focus-visible:!shadow-[0_0_0_2px_var(--focus-ring)]",
  "[aria-expanded=true]:focus-visible:!shadow-[0_0_0_1px_var(--focus-ring)]",
].join(" ");

/** Info-Block-Kopf: Mobile Titel oben, Aktionen darunter; ab sm eine Zeile */
export const entityInfoBlockHeaderShellClassName =
  "mb-3 flex min-w-0 flex-col gap-2 max-md:gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3";

export const entityInfoBlockHeaderTitleWrapClassName = "min-w-0 w-full sm:flex-1";

export const entityInfoBlockHeaderActionsClassName =
  "flex shrink-0 flex-wrap items-center justify-end gap-1 max-md:gap-1 max-md:w-full sm:gap-2";

/** Sekundär-Link im Info-Kopf (z. B. „Zum Projekt“) — Mobile gleiche Höhe wie Bearbeiten */
export const entityInfoBlockHeaderLinkClassName = [
  appButtonSecondarySmLinkClassName,
  detailPageHeaderNavSecondaryShellClassName,
  "shrink-0 justify-center text-center whitespace-nowrap no-underline",
  "min-h-[44px] sm:min-h-0",
  "max-md:!min-h-8 max-md:!h-8 max-md:!max-h-8 max-md:!px-2 max-md:!text-[11px] max-md:!font-medium",
].join(" ");

/** Bearbeiten: primary bleibt blau; Mobile nur kompakter */
export const entityInfoBlockEditButtonClassName = [
  "shrink-0 min-h-[44px] sm:min-h-0",
  "max-md:!min-h-8 max-md:!h-8 max-md:!max-h-8 max-md:!px-2.5 max-md:!text-[11px] max-md:!font-medium",
].join(" ");

/** „Mehr“-Menü: rechts am „…“-Trigger ausrichten (nicht nach rechts über den Rand) */
export const entityMoreMenuPanelClassName = [
  "absolute right-0 left-auto mt-1.5",
  "w-52 min-w-[9.5rem] max-w-[min(calc(100vw-2rem),16rem)]",
  "flex flex-col gap-1 p-1 rounded-lg border border-default shadow-lg",
  "bg-surface-elevated backdrop-blur-sm",
].join(" ");

/** Icon/Emoji rechts in Menüzeilen — kleiner als Fliesstext */
export const entityMoreMenuItemEmojiClassName =
  "shrink-0 text-[13px] leading-none opacity-90";

const ENTITY_MORE_MENU_ROW_BASE = [
  "flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-xs font-medium tracking-tight",
  "rounded-md border min-h-9 !h-auto !shadow-none",
  "focus-visible:!outline-none focus-visible:!ring-2 focus-visible:!ring-blue-400/40 focus-visible:!ring-offset-0",
  "cursor-pointer transition-colors !justify-between no-underline box-border",
].join(" ");

/** Standard-Zeile: leichte Karte, Text links / Symbol rechts */
export const entityMoreMenuItemClassName = [
  ENTITY_MORE_MENU_ROW_BASE,
  "!border-white/[0.06] !bg-white/[0.04] !text-blue-50",
  "hover:!bg-white/[0.08] hover:!border-white/[0.10] hover:!text-blue-50",
].join(" ");

/** Warn-/Sonderaktion (z. B. Konvertieren) */
export const entityMoreMenuAccentItemClassName = [
  ENTITY_MORE_MENU_ROW_BASE,
  "!text-amber-100 !border-amber-400/22 !bg-amber-500/[0.07]",
  "hover:!bg-amber-500/[0.14] hover:!border-amber-300/32",
].join(" ");

/** Löschen & Co. — gleiche Rot-Tönung wie `fileListDeleteActionClassName` / Datei-Zeile */
export const entityMoreMenuDangerItemClassName = [
  ENTITY_MORE_MENU_ROW_BASE,
  destructiveTonalInteractiveClassName,
].join(" ");

/**
 * Kompakte Listen-Zeilen: Bearbeiten / PDF / Sekundär (wie Material & FileActions compact)
 */
export const entityRowTonalActionClassName =
  "inline-flex items-center justify-center shrink-0 min-h-9 h-9 max-h-9 px-2.5 rounded-lg border border-white/15 bg-white/10 text-blue-100 text-xs font-medium hover:bg-white/15";

/** Rot umrandete Löschen-Zeile (nicht durchgehend voller Danger-Klotz) */
export const entityRowDeleteActionClassName =
  `${entityRowTonalActionClassName} ${destructiveTonalInteractiveClassName}`;

/**
 * Kompakte Tabellenzeile (Admin): feste Höhe — Bearbeiten/Löschen gleich gross.
 * Nicht mit `entityRowDeleteActionClassName` mischen (der hat Touch-min-h 44px).
 */
export const entityRowCompactActionClassName =
  "!h-7 !min-h-7 !max-h-7 !px-2.5 !py-0 text-[11px] leading-none whitespace-nowrap shrink-0 justify-center";

export const entityRowCompactDeleteActionClassName =
  `${entityRowCompactActionClassName} ${destructiveTonalInteractiveClassName}`;
